import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DesktopOutlined, ExpandOutlined, MobileOutlined } from "@ant-design/icons";
import { Button, Segmented } from "antd";
import SurveyWidgetPanel from "./SurveyWidgetPanel";
import styles from "./style.module.scss";

const SurveyPreview = ({
  name,
  description,
  questions = [],
  displayMode = "popup",
  widgetHeaderTitle = "",
  showBranding = true,
  requestContactDetails = false,
  contactDetailsRequired = false,
  primaryColor = "",
  logoUrl = "",
  popupPosition = "bottom_right",
  flowMode = "stepped",
  thankYouMessage = "",
  formatType = "text",
  // The builder's narrow column shows a button that opens the same preview large (onExpand);
  // the large one (expanded) gets more room and starts on the shared-link page.
  onExpand,
  expanded = false,
}) => {
  const { t } = useTranslation();
  const [scene, setScene] = useState("web");
  const [contextMode, setContextMode] = useState(expanded ? "standalone" : "embed");

  const titleText =
    (name || "").trim() || t("surveyManagement.preview_title_placeholder");
  const descText = (description || "").trim();

  const modeLabel = useMemo(() => {
    if (contextMode === "standalone") {
      return t("surveyManagement.preview_context_standalone");
    }
    if (displayMode === "fullscreen") {
      return t("surveyManagement.distribute.embed.fullscreen");
    }
    if (displayMode === "inline") {
      return t("surveyManagement.distribute.embed.inline");
    }
    return t("surveyManagement.distribute.embed.popup");
  }, [contextMode, displayMode, t]);

  const popupPositionClass =
    popupPosition === "bottom_left"
      ? styles.previewSurvey_popupLeft
      : styles.previewSurvey_popup;

  const panel = (
    <SurveyWidgetPanel
      title={titleText}
      description={descText}
      questions={questions}
      widgetHeaderTitle={widgetHeaderTitle}
      logoUrl={logoUrl}
      showBranding={showBranding}
      requestContactDetails={requestContactDetails}
      contactDetailsRequired={contactDetailsRequired}
      primaryColor={primaryColor}
      flowMode={flowMode}
      thankYouMessage={thankYouMessage}
      previewMode
      compact={contextMode === "embed"}
      maxPreviewQuestions={4}
    />
  );

  return (
    <div className={[styles.previewShell, expanded ? styles.previewShellExpanded : ""].filter(Boolean).join(" ")}>
      <div className={styles.previewToolbar}>
        <span className={styles.previewTitle}>
          {t("surveyManagement.preview_title")}
        </span>
        <Segmented
          size="small"
          value={scene}
          onChange={setScene}
          options={[
            {
              label: t("surveyManagement.preview_web"),
              value: "web",
              icon: <DesktopOutlined />,
            },
            {
              label: t("surveyManagement.preview_app"),
              value: "mobile",
              icon: <MobileOutlined />,
            },
          ]}
        />
      </div>

      <div className={styles.previewContextRow}>
        <Segmented
          size="small"
          value={contextMode}
          onChange={setContextMode}
          options={[
            {
              label: t("surveyManagement.preview_context_embed"),
              value: "embed",
            },
            {
              label: t("surveyManagement.preview_context_standalone"),
              value: "standalone",
            },
          ]}
        />
        {onExpand ? (
          <Button size="small" icon={<ExpandOutlined />} onClick={onExpand}>
            {t("surveyManagement.preview_expand")}
          </Button>
        ) : null}
      </div>

      <p className={styles.previewCaption}>
        {contextMode === "standalone"
          ? t("surveyManagement.preview_caption_standalone")
          : t("surveyManagement.preview_caption_embed", { mode: modeLabel })}
      </p>

      {contextMode === "standalone" ? (
        <div
          className={[
            styles.previewStandaloneShell,
            scene === "mobile" ? styles.previewStandaloneMobile : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {panel}
        </div>
      ) : scene === "web" ? (
        <div className={styles.previewBrowser}>
          <div className={styles.previewBrowserChrome}>
            <span className={styles.previewDot} />
            <span className={styles.previewDot} />
            <span className={styles.previewDot} />
            <span className={styles.previewUrl}>www.your-site.com/feedback</span>
          </div>
          <div className={styles.previewBrowserBody}>
            <div className={styles.previewFakePage}>
              <div className={styles.previewFakeHero} />
              <div className={styles.previewFakeLine} />
              <div className={styles.previewFakeLineShort} />
              {displayMode === "inline" ? (
                <div className={styles.previewInlineSlot}>{panel}</div>
              ) : (
                <div
                  className={[
                    styles.previewSurvey,
                    popupPositionClass,
                    displayMode === "fullscreen"
                      ? styles.previewSurvey_fullscreen
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {panel}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.previewPhone}>
          <div className={styles.previewPhoneNotch} />
          <div className={styles.previewPhoneScreen}>
            <div className={styles.previewAppHeader}>
              {t("surveyManagement.preview_app_name")}
            </div>
            <div className={styles.previewAppContent}>
              <div className={styles.previewFakeLine} />
              <div className={styles.previewFakeLineShort} />
            </div>
            <div
              className={[
                styles.previewSurvey,
                popupPositionClass,
                styles.previewSurveyMobile,
              ].join(" ")}
            >
              {panel}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyPreview;
